import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Camera } from "lucide-react";

/**
 * /anzeige/pdl
 * Drei Editorial-Poster für die PDL-Weiterbildung (720 UE).
 * Zielgruppe: Inhaber & PDL, die ihre beste Pflegekraft zur Führung entwickeln.
 * Eigene Fotoserie (Gemini Nano Banana) — kein Stock, kein Klischee. 4:5 für Meta Feed.
 */

const LOGO = "/dh_logo_transparent.png";
const IMG_MENTORING = "/ads/pdl_mentoring.png";
const IMG_DOORWAY   = "/ads/pdl_doorway.png";
const IMG_STILL     = "/ads/pdl_stilllife.png";

export default function AnzeigePdl() {
  const [variant, setVariant] = useState("all");
  return (
    <div className="min-h-screen bg-espresso-deep text-cream-50">
      <div className="border-b border-white/10 bg-espresso-deep/80 backdrop-blur-sm sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-3 flex-wrap">
          <Link
            to="/"
            data-testid="anzeige-pdl-back"
            className="inline-flex items-center gap-1.5 text-[13px] text-cream-100 hover:text-saffron"
          >
            <ArrowLeft className="h-3.5 w-3.5" /> zurück
          </Link>
          <div className="flex items-center gap-1.5 flex-wrap">
            <VariantBtn active={variant === "all"} onClick={() => setVariant("all")} label="Alle" />
            <VariantBtn active={variant === "v1"} onClick={() => setVariant("v1")} label="V1 · Schlüssel" />
            <VariantBtn active={variant === "v2"} onClick={() => setVariant("v2")} label="V2 · Testimonial" />
            <VariantBtn active={variant === "v3"} onClick={() => setVariant("v3")} label="V3 · Türrahmen" />
          </div>
          <div className="text-[11px] text-cream-100/60 inline-flex items-center gap-1">
            <Camera className="h-3 w-3" /> 4:5 · Screenshot für Ad-Manager
          </div>
        </div>
      </div>

      <div className="px-4 sm:px-6 py-10 sm:py-14">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-8 sm:mb-10">
            <div className="inline-flex items-center rounded-full border border-white/20 text-cream-100/80 text-[10px] font-bold tracking-widest uppercase px-3 py-1 mb-3">
              PDL-Weiterbildung · für Inhaber &amp; Träger · kein Stock
            </div>
            <h1 className="font-display text-4xl sm:text-5xl leading-tight">
              Führung wächst <span className="text-saffron">im eigenen Team.</span>
            </h1>
          </div>

          <div className={`grid ${variant === "all" ? "grid-cols-1 md:grid-cols-3" : "grid-cols-1 justify-items-center"} gap-6 sm:gap-8 items-start`}>
            {(variant === "all" || variant === "v1") && (
              <div className="w-full max-w-[420px] mx-auto">
                <PosterLabel n="V1" name="Schlüsselübergabe" />
                <V1Schluessel />
              </div>
            )}
            {(variant === "all" || variant === "v2") && (
              <div className="w-full max-w-[420px] mx-auto">
                <PosterLabel n="V2" name="Still-Life · Testimonial" />
                <V2Testimonial />
              </div>
            )}
            {(variant === "all" || variant === "v3") && (
              <div className="w-full max-w-[420px] mx-auto">
                <PosterLabel n="V3" name="Türrahmen · Full Bleed" />
                <V3Tuerrahmen />
              </div>
            )}
          </div>

          {/* Verkaufsclip · Sora 2 · 9:16 */}
          <div className="mt-14 sm:mt-20 text-center">
            <div className="inline-flex items-center rounded-full border border-white/20 text-cream-100/80 text-[10px] font-bold tracking-widest uppercase px-3 py-1 mb-3">
              Verkaufsclip · 9:16 · für Reels &amp; Meta-Ads
            </div>
            <h2 className="font-display text-3xl sm:text-4xl leading-tight mb-8">
              Die Schlüsselübergabe. <span className="text-saffron">Als Film.</span>
            </h2>
            <div className="max-w-[380px] mx-auto">
              <video
                src="/ads/pdl_verkaufsclip.mp4"
                poster="/ads/pdl_verkaufsclip_poster.jpg"
                data-testid="pdl-verkaufsclip"
                className="w-full aspect-[9/16] rounded-2xl object-cover shadow-[0_25px_60px_-20px_rgba(0,0,0,0.7)] border border-white/10"
                controls
                playsInline
                preload="metadata"
              />
              <a
                href="/ads/pdl_verkaufsclip.mp4"
                download="dickies-helden-pdl-verkaufsclip.mp4"
                data-testid="pdl-verkaufsclip-download"
                className="mt-5 inline-flex items-center gap-2 rounded-full bg-saffron text-espresso-deep px-5 py-2.5 text-[13px] font-black hover:opacity-90 transition-opacity"
              >
                Clip herunterladen (MP4)
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ================================================================
 * V1 · SCHLÜSSELÜBERGABE — Foto oben (58%), Cream unten.
 * Emotion: Vertrauen weitergeben. Copy an Inhaber gerichtet.
 * ================================================================ */
function V1Schluessel() {
  return (
    <div
      data-testid="ad-pdl-v1"
      className="relative w-full aspect-[4/5] rounded-2xl overflow-hidden bg-cream-50 shadow-[0_25px_60px_-20px_rgba(0,0,0,0.7)] flex flex-col group"
    >
      <div className="relative h-[58%] overflow-hidden">
        <img
          src={IMG_MENTORING}
          alt=""
          className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1600ms] ease-out group-hover:scale-[1.03]"
          draggable={false}
        />
        <div className="absolute inset-0 bg-terra/10 mix-blend-multiply pointer-events-none" />
        <div
          className="absolute inset-0 opacity-[0.09] mix-blend-overlay pointer-events-none"
          style={{ backgroundImage: "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='120' height='120'><filter id='n'><feTurbulence baseFrequency='0.9'/></filter><rect width='100%' height='100%' filter='url(%23n)' opacity='0.6'/></svg>\")" }}
        />

        <div className="absolute top-4 left-5 right-5 flex items-center justify-between text-[9.5px] uppercase tracking-[0.28em] font-bold">
          <span className="inline-flex items-center gap-2">
            <DhLogoChip />
            <span className="text-cream-50 mix-blend-difference">Dickies-Campus · Nº 08</span>
          </span>
          <span className="text-cream-50 mix-blend-difference">MMXXVI</span>
        </div>

        <div className="absolute bottom-3 left-5 right-5 flex items-center justify-between gap-2">
          <span className="text-[10px] font-bold uppercase tracking-[0.24em] text-cream-50 bg-espresso-deep/85 rounded-full px-2.5 py-1">
            für Inhaber &amp; Träger
          </span>
          <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-[0.20em] text-espresso-deep bg-saffron rounded-full px-2.5 py-1">
            720 UE · anerkannt
          </span>
        </div>
      </div>

      <div className="flex-1 relative px-6 pt-4 pb-5 flex flex-col">
        <div className="absolute -top-[1px] left-6 h-[3px] w-14 bg-terra" />

        <div className="flex items-center gap-2 mb-2">
          <PflegeMark />
          <span className="text-[9.5px] font-bold uppercase tracking-[0.24em] text-terra">
            Weiterbildung Pflegedienstleitung
          </span>
        </div>

        <h2 className="font-display text-espresso-deep font-black tracking-[-0.02em] leading-[0.95] text-[32px] mb-2">
          Wer führt, <br/>
          <span className="italic text-terra">wenn du loslässt?</span>
        </h2>
        <p className="text-[12px] leading-[1.45] text-sand-700 max-w-[300px] mb-3">
          Deine beste Pflegekraft hat es längst verdient. Die anerkannte{" "}
          <strong className="text-espresso-deep">PDL-Weiterbildung</strong> — berufsbegleitend,
          E-Learning + Live, Start jeden Montag.
        </p>

        <div className="mt-auto flex items-end justify-between gap-3">
          <div className="inline-flex items-center gap-1.5 rounded-full bg-espresso-deep text-cream-50 px-4 py-2 text-[12px] font-bold">
            Beratungsgespräch
            <span className="inline-block w-1 h-1 rounded-full bg-saffron" />
          </div>
          <div className="text-right leading-tight">
            <div className="text-[8.5px] uppercase tracking-[0.24em] font-bold text-espresso-deep/60">ca. 36 Wochen</div>
            <div className="font-display text-[20px] leading-none font-black text-brand-darkred">
              berufsbegleitend
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ================================================================
 * V2 · STILL-LIFE + TESTIMONIAL — Split. Oben Schlüssel-Still-Life,
 * unten Terra mit Inhaber-Zitat.
 * ================================================================ */
function V2Testimonial() {
  return (
    <div
      data-testid="ad-pdl-v2"
      className="relative w-full aspect-[4/5] rounded-2xl overflow-hidden shadow-[0_25px_60px_-20px_rgba(0,0,0,0.7)] flex flex-col"
    >
      <div className="relative h-[50%] overflow-hidden">
        <img src={IMG_STILL} alt="" className="w-full h-full object-cover" draggable={false} />
        <div className="absolute inset-0 bg-espresso-deep/10 mix-blend-multiply pointer-events-none" />

        <div className="absolute top-4 left-5 inline-flex items-center gap-1.5 rounded-full bg-cream-50/95 text-espresso-deep pl-1 pr-2.5 py-1 shadow">
          <img src={LOGO} alt="Dickies-Campus" className="h-4 w-4 object-contain" draggable={false} />
          <span className="text-[9.5px] font-bold uppercase tracking-[0.24em]">
            PDL · 720 UE · anerkannt
          </span>
        </div>

        <div className="absolute top-4 right-5 text-[9px] font-bold uppercase tracking-[0.32em] text-cream-50 bg-espresso-deep/70 rounded-full px-2.5 py-1">
          Nº 08 · MMXXVI
        </div>
      </div>

      <div className="relative flex-1 bg-terra text-cream-50 px-6 pt-5 pb-5 flex flex-col">
        <div className="absolute -top-[1px] left-0 right-0 h-[1px] bg-cream-50/40" />
        <div className="absolute top-2 left-5 font-display text-[80px] leading-none text-saffron/40 select-none">
          „
        </div>

        <blockquote className="pl-8 pr-3 font-display italic text-[16.5px] leading-[1.15] text-cream-50">
          Ich habe meiner besten Fachkraft die PDL-Weiterbildung geschenkt. Heute führt sie das Team — und ich habe endlich wieder Zeit fürs Unternehmen.
        </blockquote>

        <div className="pl-8 mt-2 text-[10.5px] uppercase tracking-[0.24em] font-bold text-cream-50/80">
          — Thomas K., Inhaber Ambulanter Dienst
        </div>

        <div className="mt-auto flex items-end justify-between gap-3">
          <div className="leading-tight">
            <div className="flex items-center gap-1.5 mb-1">
              <span className="inline-flex items-center justify-center h-5 w-5 rounded-full bg-cream-50 shadow">
                <img src={LOGO} alt="" className="h-3.5 w-3.5 object-contain" draggable={false} />
              </span>
              <span className="text-[9px] uppercase tracking-[0.28em] font-bold text-cream-50/70">
                Weiterbildung Pflegedienstleitung
              </span>
            </div>
            <div className="font-display font-black text-[20px] leading-none tracking-tight">
              Start jeden Montag
            </div>
          </div>
          <div className="inline-flex items-center gap-1.5 rounded-full bg-cream-50 text-espresso-deep px-4 py-2 text-[12px] font-bold shadow">
            Beratungsgespräch
            <span className="inline-block w-1 h-1 rounded-full bg-terra" />
          </div>
        </div>
      </div>
    </div>
  );
}

/* ================================================================
 * V3 · TÜRRAHMEN — Full-Bleed. Die neue Führungskraft im Licht.
 * Emotion: Stolz & Aufstieg — aus Sicht des Inhabers.
 * ================================================================ */
function V3Tuerrahmen() {
  return (
    <div
      data-testid="ad-pdl-v3"
      className="relative w-full aspect-[4/5] rounded-2xl overflow-hidden shadow-[0_25px_60px_-20px_rgba(0,0,0,0.7)] bg-espresso-deep group"
    >
      <img
        src={IMG_DOORWAY}
        alt=""
        className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1600ms] ease-out group-hover:scale-[1.03]"
        draggable={false}
      />
      <div className="absolute inset-0 bg-gradient-to-b from-espresso-deep/40 via-transparent to-espresso-deep/95" />
      <div className="absolute inset-0 bg-terra/8 mix-blend-multiply pointer-events-none" />
      <div
        className="absolute inset-0 opacity-[0.12] pointer-events-none mix-blend-overlay"
        style={{ backgroundImage: "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='120' height='120'><filter id='n'><feTurbulence baseFrequency='0.9'/></filter><rect width='100%' height='100%' filter='url(%23n)' opacity='0.7'/></svg>\")" }}
      />

      <div className="absolute top-5 left-5 right-5 flex items-center justify-between text-[9.5px] uppercase tracking-[0.28em] font-bold text-cream-50/85">
        <span className="inline-flex items-center gap-2">
          <DhLogoChip />
          <span>Dickies-Campus · Nº 08</span>
        </span>
        <span className="text-saffron">MMXXVI</span>
      </div>

      <div className="absolute top-14 left-5">
        <div className="inline-flex items-center gap-1.5 rounded-full bg-saffron text-espresso-deep px-3 py-1.5 shadow-lg">
          <PflegeMark />
          <span className="text-[10px] font-black uppercase tracking-[0.22em]">
            PDL-Weiterbildung · 720 UE
          </span>
        </div>
      </div>

      <div className="absolute inset-x-0 bottom-0 p-6 pb-6">
        <div className="inline-flex items-center gap-1.5 rounded-full bg-espresso-deep/80 backdrop-blur-sm text-saffron px-2.5 py-1 text-[9.5px] font-bold uppercase tracking-[0.24em] mb-3">
          für Inhaber · anerkannt · berufsbegleitend
        </div>

        <h2 className="font-display font-black tracking-[-0.03em] leading-[0.86] text-cream-50 drop-shadow-[0_2px_8px_rgba(0,0,0,0.5)]">
          <span className="block text-[38px]">Aus deiner besten</span>
          <span className="block text-[38px] italic">Pflegekraft wird</span>
          <span className="block text-[38px] underline decoration-[3px] decoration-saffron underline-offset-[8px]">deine PDL.</span>
        </h2>

        <p className="mt-3 max-w-[290px] text-[12.5px] text-cream-100/85 leading-relaxed">
          <strong className="text-cream-50">720 UE, E-Learning + Live</strong> — neben dem Dienstplan.{" "}
          <span className="text-saffron font-black">Start jeden Montag.</span>
        </p>

        <div className="mt-4 flex items-end justify-between gap-3">
          <div className="inline-flex items-center gap-1.5 rounded-full bg-saffron text-espresso-deep px-4 py-2 text-[12px] font-black shadow">
            Beratungsgespräch anfragen
            <span className="inline-block w-1.5 h-1.5 rounded-full bg-brand-darkred" />
          </div>
          <div className="text-right leading-tight">
            <div className="text-[8px] uppercase tracking-[0.28em] font-bold text-cream-50/60">Web</div>
            <div className="text-[10.5px] font-black tracking-tight text-cream-50">dickies-helden.de</div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* DH-Logo in kleinem Cream-Chip · funktioniert auf Foto & dunklem Grund. */
function DhLogoChip() {
  return (
    <span className="inline-flex items-center justify-center h-6 w-6 rounded-full bg-cream-50/95 shadow-md">
      <img src={LOGO} alt="Dickies-Campus" className="h-4 w-4 object-contain" draggable={false} />
    </span>
  );
}

/* Pflege-Kreuz-Mark · signalisiert "Pflege" ohne Klischee. */
function PflegeMark({ size = 14 }) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} className="shrink-0" aria-hidden="true">
      <circle cx="12" cy="12" r="11" fill="currentColor" opacity="0.18" />
      <path d="M10 5h4v5h5v4h-5v5h-4v-5H5v-4h5V5z" fill="currentColor" />
    </svg>
  );
}

function PosterLabel({ n, name }) {
  return (
    <div className="mb-3 flex items-center gap-2 text-cream-50">
      <span className="inline-flex items-center justify-center h-7 min-w-7 px-2 rounded-full bg-saffron text-espresso-deep font-display font-black text-[13px]">
        {n}
      </span>
      <span className="font-display text-lg">{name}</span>
    </div>
  );
}

function VariantBtn({ active, onClick, label }) {
  return (
    <button
      onClick={onClick}
      className={`rounded-full px-3 py-1 text-[12px] font-semibold transition-colors ${
        active
          ? "bg-saffron text-espresso-deep"
          : "border border-white/20 text-cream-100/80 hover:border-saffron/60"
      }`}
    >
      {label}
    </button>
  );
}
